import TableWrapper from "components/common/table-wrapper";
import { Plus, Pencil } from "phosphor-react";
import { useEffect, useState, useCallback, useMemo } from "react";
import Tooltip from "components/common/tooltip";
import { DEBOUNCE_DELAY, initialMetaForTable } from "constants/common";
import CustomModal from "components/common/modal";
import AddApprovalConfigrationForm from "./AddApprovalConfigrationForm";
import {
  getUserRole,
  canViewOnlyConfiguration,
  capitalizeName,
  highlightFirstMatch,
} from "utils/common";
import {
  deleteApprovalConfiguration,
  getApprovalConfigurations,
} from "containers/configuration/api";
import debounce from "lodash/debounce";

const PaymentMethodConfiguration = () => {
  const [search, setSearch] = useState("");
  const [selectedItems, setSelectedItems] = useState(0);
  const [meta, setMeta] = useState({
    ...initialMetaForTable,
    mif_status: "active",
    perPage: "10",
  });
  const [isApprovalModalVisible, setIsApprovalModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [loading, setLoading] = useState(false);
  const [count, setCount] = useState(0);
  const [approvalConfigurations, setApprovalConfigurations] = useState([]);
  const [refreshPage, setRefreshPage] = useState(false);

  const userRole = getUserRole();
  const isViewOnly = canViewOnlyConfiguration(userRole);

  const fetchApprovalConfigurationList = async (payload) => {
    setLoading(true);
    try {
      const result = await getApprovalConfigurations(payload);
      if (result?.approval_configurations) {
        setApprovalConfigurations(result.approval_configurations);
        setCount(result?.meta?.total_count || 0);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };
  const handleSetSearchQuery = (value) => {
    setMeta((prev) => ({
      ...prev,
      search: value,
      page: 1,
    }));
  };

  const debouncedSearch = useCallback(
    debounce(handleSetSearchQuery, DEBOUNCE_DELAY),
    [],
  );

  useEffect(() => {
    const payload = {
      ...meta,
      order: meta.order,
      sort: meta.sort,
    };
    fetchApprovalConfigurationList(payload);
  }, [meta, refreshPage]);

  const transformedMethods = useMemo(() => {
    return approvalConfigurations
      .map((item) => {
        const coordinatorRows =
          item.approval_program_coordinators?.map((approvalConfiguration) => ({
            originalConfig: item,
            method: item.approval_method?.trim(),

            state_name: approvalConfiguration.state?.name || "N/A",
            county_name: approvalConfiguration.county?.name || "N/A",

            coordinator_email: approvalConfiguration.email,
            cs_manager: item.cs_manager_required
              ? item.cs_manager_email || "Required"
              : "Not Required",
            executive: item.executive_approval_required
              ? item.executive_email || "Required"
              : "Not Required",
            accountant: item.accounting_approval_required
              ? item.accounting_approval_email || "Required"
              : "Not Required",
          })) || [];

        if (coordinatorRows.length === 0) {
          coordinatorRows.push({
            originalConfig: item,
            method: item.approval_method?.trim(),
            state_name: "-",
            county_name: "-",
            coordinator_email: "-",
            cs_manager: item.cs_manager_required ? "Required" : "Not Required",
            executive: item.executive_approval_required
              ? item.executive_email || "Required"
              : "Not Required",
            accountant: item.accounting_approval_required
              ? item.accounting_approval_email || "Required"
              : "Not Required",
          });
        }

        return coordinatorRows;
      })
      .flat();
  }, [approvalConfigurations]);

  const groupedMethods = useMemo(() => {
    return transformedMethods.reduce((acc, item) => {
      if (!acc[item.method]) acc[item.method] = [];
      acc[item.method].push(item);

      return acc;
    }, {});
  }, [transformedMethods]);
  const handleRefreshPage = () => {
    setRefreshPage((prev) => !prev);
  };

  const handleApprovalConfiguration = async (id) => {
    try {
      const result = await deleteApprovalConfiguration(id);
      if (result) fetchApprovalConfigurationList(meta);
    } catch (error) {
      console.log(error);
    }
  };

  const handlePageChange = (page) => {
    setMeta((prev) => ({ ...prev, page }));
  };

  return (
    <>
      <TableWrapper
        searchPlaceholder="Search Payment Method"
        searchValue={search}
        setSearhQuery={(query) => {
          setSearch(query);
          debouncedSearch(query);
        }}
        totalListCount={count}
        pageSize={meta.perPage}
        currentPage={meta.page}
        onPageChange={handlePageChange}
        selectedItems=""
        isLoading={loading}
        meta={meta}
        setMeta={setMeta}
        handleRefreshPage={() => fetchApprovalConfigurationList(meta)}
        actionButtons={
          isViewOnly
            ? []
            : selectedItems > 0
            ? []
            : [
                {
                  label: "Add Payment Method",
                  classes: "primary-btn",
                  icon: <Plus size={24} className="me-3" />,
                  handleClick: () => setIsApprovalModalVisible(true),
                },
              ]
        }
      >
        <div className="container-fluid">
          <div className="overflow-scroll border-radius-20px">
            <table className="table table-rows">
              <thead
                className="table-header"
                style={{ backgroundColor: "black" }}
              >
                <tr>
                  <th style={{ borderRight: "1px solid #DADADA" }}>
                    Payment Method
                  </th>
                  <th colSpan={7} style={{ textAlign: "center" }}>
                    Approval Levels
                  </th>
                </tr>

                <tr>
                  <th style={{ borderRight: "1px solid #DADADA" }}></th>

                  <th
                    colSpan={3}
                    style={{
                      textAlign: "center",
                      borderRight: "1px solid #DADADA",
                    }}
                  >
                    Program Coordinator
                  </th>

                  <th
                    style={{
                      textAlign: "center",
                      borderRight: "1px solid #DADADA",
                    }}
                  >
                    CS Manager
                  </th>
                  <th
                    style={{
                      textAlign: "center",
                      borderRight: "1px solid #DADADA",
                    }}
                  >
                    Executive
                  </th>
                  <th
                    style={{
                      textAlign: "center",
                      borderRight: "1px solid #DADADA",
                    }}
                  >
                    Accountant
                  </th>
                  <th style={{ textAlign: "center" }}></th>
                </tr>
              </thead>

              <tbody>
                {Object.entries(groupedMethods).map(([method, rows]) =>
                  rows.map((row, rowIndex) => (
                    <tr key={`${method}-${rowIndex}`}>
                      {rowIndex === 0 && (
                        <td
                          className="main-heading"
                          rowSpan={rows.length}
                          style={{
                            borderRight: "1px solid #DADADA",
                            verticalAlign: "top",
                          }}
                        >
                          {highlightFirstMatch(method, meta.search)}
                        </td>
                      )}
                      <td style={{ borderRight: "1px solid #DADADA" }}>
                        {row.state_name?.toUpperCase()}
                      </td>

                      <td style={{ borderRight: "1px solid #DADADA" }}>
                        {capitalizeName(row.county_name)}
                      </td>
                      <td style={{ borderRight: "1px solid #DADADA" }}>
                        {row.coordinator_email}
                      </td>
                      {rowIndex === 0 && (
                        <td
                          rowSpan={rows.length}
                          style={{
                            borderRight: "1px solid #DADADA",
                            verticalAlign: "top",
                          }}
                        >
                          {row.cs_manager}
                        </td>
                      )}
                      {rowIndex === 0 && (
                        <td
                          rowSpan={rows.length}
                          style={{
                            borderRight: "1px solid #DADADA",
                            verticalAlign: "top",
                          }}
                        >
                          {row.executive}
                        </td>
                      )}
                      {rowIndex === 0 && (
                        <td
                          rowSpan={rows.length}
                          style={{
                            borderRight: "1px solid #DADADA",
                            verticalAlign: "top",
                          }}
                        >
                          {row.accountant}
                        </td>
                      )}
                      {rowIndex === 0 && (
                        <td
                          rowSpan={rows.length}
                          style={{
                            textAlign: "center",
                            verticalAlign: "top",
                          }}
                        >
                          {!isViewOnly && (
                            <>
                              <Tooltip title="Edit">
                                <Pencil
                                  size={24}
                                  className="opacity-50 me-4"
                                  style={{ cursor: "pointer" }}
                                  onClick={() => {
                                    setSelectedPaymentMethod(
                                      row.originalConfig,
                                    );
                                    setIsEditModalVisible(true);
                                  }}
                                />
                              </Tooltip>

                              {/* <Tooltip title="Delete">
                                <Trash
                                  size={24}
                                  className="opacity-50 me-4"
                                  style={{ cursor: "pointer" }}
                                  onClick={() => {
                                    if (row.originalConfig?.id) {
                                      handleApprovalConfiguration(
                                        row.originalConfig.id,
                                      );
                                    }
                                  }}
                                />
                              </Tooltip> */}
                            </>
                          )}
                        </td>
                      )}
                    </tr>
                  )),
                )}
              </tbody>
            </table>
          </div>
        </div>
      </TableWrapper>

      {isApprovalModalVisible && (
        <CustomModal
          size="lg"
          show
          onHide={() => setIsApprovalModalVisible(false)}
          heading="Add Payment Method"
        >
          <AddApprovalConfigrationForm
            handleClose={() => {
              setIsApprovalModalVisible(false);
              setRefreshPage(!refreshPage);
            }}
          />
        </CustomModal>
      )}

      {isEditModalVisible && selectedPaymentMethod && (
        <CustomModal
          size="lg"
          show
          onHide={() => {
            setIsEditModalVisible(false);
            setSelectedPaymentMethod(null);
          }}
          heading="Edit Payment Method"
        >
          <AddApprovalConfigrationForm
            editId={selectedPaymentMethod.id}
            editData={selectedPaymentMethod}
            handleClose={() => {
              setIsEditModalVisible(false);
              setSelectedPaymentMethod(null);
              setRefreshPage(!refreshPage);
            }}
          />
        </CustomModal>
      )}
    </>
  );
};

export default PaymentMethodConfiguration;
